import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { XIcon } from "@/components/ds/icons"

import { cn } from "@/lib/utils"

function Dialog({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />
}

function DialogTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        // Overlay del DS: rgba(9,9,11,.35). Nunca negro puro.
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-[rgba(9,9,11,0.35)]",
        className
      )}
      {...props}
    />
  )
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  onPointerDownOutside,
  onInteractOutside,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean
}) {
  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        // El clic fuera no cierra la modal: suele ser el cierre de un
        // popover/select interno y cerraba la modal completa, perdiendo el
        // formulario en curso. ESC sí cierra (con un popover abierto, Radix
        // entrega ESC solo a la capa más alta, así que cierra el popover y no
        // la modal). Pasar un handler propio restaura el default de Radix.
        onPointerDownOutside={onPointerDownOutside ?? ((e) => e.preventDefault())}
        onInteractOutside={onInteractOutside ?? ((e) => e.preventDefault())}
        className={cn(
          // Chrome del DS: la modal NO lleva padding propio. El padding vive en
          // DialogHeader / DialogBody / DialogFooter para que sus divisores y el
          // fondo del pie lleguen de borde a borde. `overflow-hidden` + el
          // `overflow-y-auto` de DialogBody hacen que scrollee SOLO el cuerpo:
          // el encabezado y el pie quedan fijos.
          "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 flex w-full max-w-[calc(100%-2rem)] max-h-[calc(100vh-3rem)] translate-x-[-50%] translate-y-[-50%] flex-col overflow-hidden rounded-xl border p-0 shadow-[var(--shadow-modal)] duration-200 outline-none sm:max-w-[520px]",
          className
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            // 30x30 radio 7, alineado con el padding del encabezado. Va absoluto
            // (no dentro del flujo del header) para no obligar a cada llamador a
            // componerlo; DialogHeader reserva el espacio con su `pr`.
            className="text-muted-foreground hover:bg-accent hover:text-accent-foreground focus:ring-ring absolute top-[13px] right-[14px] flex size-[30px] items-center justify-center rounded-[7px] transition-colors focus:ring-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-[19px]"
          >
            <XIcon className="size-[19px]" />
            <span className="sr-only">Cerrar</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
}

/**
 * Encabezado: título + subtítulo, con divisor de borde a borde. El `pr` deja
 * libre la esquina donde `DialogContent` posiciona el botón de cerrar.
 */
function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn(
        "border-border-subtle flex shrink-0 flex-col gap-0.5 border-b px-[18px] py-4 pr-[52px] text-left",
        className
      )}
      {...props}
    />
  )
}

/**
 * Cuerpo de la modal — la ÚNICA parte que scrollea.
 *
 * Existe para que el llamador no vuelva a escribir el padding a mano: antes
 * cada modal repetía `px-4 pb-4 md:px-0 md:pb-0` para reconciliar el padding
 * del drawer (móvil) con el de la modal (escritorio). Eso ahora vive acá.
 */
function DialogBody({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-body"
      className={cn("flex min-h-0 flex-1 flex-col gap-[18px] overflow-y-auto p-[18px]", className)}
      {...props}
    />
  )
}

/**
 * Pie sobre `--surface-muted`, con divisor superior. En escritorio: hint a la
 * izquierda (ver `DialogFooterHint`) y botones a la derecha; en móvil apila,
 * con la acción primaria arriba (`flex-col-reverse` sobre el orden del DOM
 * Cancelar → Guardar).
 */
function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "border-border-subtle bg-surface-muted flex shrink-0 flex-col-reverse gap-2 border-t px-[18px] py-[13px] sm:flex-row sm:items-center sm:justify-end sm:gap-2.5",
        className
      )}
      {...props}
    />
  )
}

/**
 * Texto explicativo a la izquierda del pie ("Los avisos llegan por correo a…").
 * Empuja los botones a la derecha con `flex-1`. Se oculta en móvil: en una
 * columna apilada, un párrafo sobre los botones sólo agrega ruido.
 */
function DialogFooterHint({ className, ...props }: React.ComponentProps<"p">) {
  return (
    <p
      data-slot="dialog-footer-hint"
      className={cn(
        "text-meta hidden text-xs leading-[1.4] sm:block sm:flex-1",
        className
      )}
      {...props}
    />
  )
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn(
        "text-base leading-none font-semibold tracking-[-0.015em]",
        className
      )}
      {...props}
    />
  )
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("text-meta text-[12.5px] leading-[1.45]", className)}
      {...props}
    />
  )
}

export {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogFooterHint,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}
